import React, { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import AuthenticatedLayout from "../../layout/AuthenticatedLayout"
import versionOneImage from "../../images/version_one.svg"
import versionTwoImage from "../../images/version_two.svg"
import versionThreeImage from "../../images/version_three.svg"
import "./SelectCanvasBoard.scss"

type BoardKey = "version_one" | "version_two" | "version_three"

interface Board {
  key: BoardKey
  title: string
  description: string
  image?: string
}

const SelectCanvasBoard = (): React.JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selected, setSelected] = useState<BoardKey | null>(null)

  const boards: Board[] = useMemo(() => [
    {
      key: "version_one",
      title: t("selectedCanvas.boardTypes.version_one.title"),
      description: t("selectedCanvas.boardTypes.version_one.description"),
      image: versionOneImage,
    },
    {
      key: "version_two",
      title: t("selectedCanvas.boardTypes.version_two.title"),
      description: t("selectedCanvas.boardTypes.version_two.description"),
      image: versionTwoImage,
    },
    {
      key: "version_three",
      title: t("selectedCanvas.boardTypes.version_three.title"),
      description: t("selectedCanvas.boardTypes.version_three.description"),
      image: versionThreeImage,
    },
  ], [t])

  const handleAdvance = () => {
    if (!selected) return
    navigate("/feedbacks/new/form", { state: { board_type: selected } })
  }

  return (
    <AuthenticatedLayout>
      <div className="container d-flex flex-column py-4 select-canvas-container">
        {/** Bloco 1: Título e subtítulo **/}
        <div className="row mb-4">
          <div className="col text-center">
            <h2 className="m-0">{t("selectedCanvas.title")}</h2>
            <p className="text-muted m-0">{t("selectedCanvas.subtitle")}</p>
          </div>
        </div>

        {/** Bloco 2: Cards + Botões (centrado verticalmente) **/}
        <div className="flex-grow-1 d-flex align-items-center">
          <div className="w-100">
            <div className="select-canvas-cards-container">
              <div className="row g-4 justify-content-center w-100">
                {boards.map((b) => (
                  <div className="col-auto" key={b.key}>
                    <button
                      type="button"
                      className={`text-start bg-white board-card ${selected === b.key ? "board-card--selected" : ""}`}
                      onClick={() => setSelected(b.key)}
                    >
                      <div className="board-card__thumb">
                        {b.image ? (
                          <img src={b.image} alt={b.title} className="board-card__image" />
                        ) : null}
                      </div>
                      <div className="fw-semibold mb-1">{b.title}</div>
                      <div className="text-muted small">{b.description}</div>
                    </button>
                  </div>
                ))}
              </div>

              <div className="select-canvas-buttons-container">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                  {t("selectedCanvas.buttons.cancel")}
                </button>
                <button type="button" className="btn btn-primary" disabled={!selected} onClick={handleAdvance}>
                  {t("selectedCanvas.buttons.advance")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default SelectCanvasBoard
